<template>
  <div class="mt-1">
    <b-form>
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label-for="wireless">
            <b-form-checkbox
              id="wireless"
              :checked="zoneForm.wireless === 'Y'"
              @change="handleWirelessChange"
              :disabled="isSaving"
            >
              Wireless
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="zoneForm.wireless === 'Y'">
        <b-col cols="12" md="6">
          <b-form-group label="Serial Number" label-for="dmpWirelessSerial">
            <validation-provider
              #default="{ errors }"
              name="Serial Number"
            >
              <b-form-input
                id="dmpWirelessSerial"
                v-model="zoneForm.dmpWirelessSerial"
                type="text"
                :state="errors.length > 0 ? false : null"
                maxlength="8"
                :disabled="isSaving || !wirelessFields.dmpWirelessSerial"
                @blur="handleSerialNumberChange"
              />
              <small class="form-text text-muted">
                {{ serialNumberHint }}
              </small>
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </validation-provider>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Contact" label-for="dmpWirelessContact">
            <b-form-select
              id="dmpWirelessContact"
              v-model="zoneForm.dmpWirelessContact"
              :options="contactOptions"
              :disabled="isSaving || !wirelessFields.dmpWirelessContact"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Normally Open" label-for="normallyOpen">
            <b-form-checkbox
              id="normallyOpen"
              :checked="zoneForm.normallyOpen === 'Y'"
              @change="zoneForm.normallyOpen = $event ? 'Y' : 'N'"
              :disabled="isSaving || !wirelessFields.normallyOpen"
            >
              Normally Open
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Supervision Time" label-for="dmpWirelessSupervision">
            <b-form-select
              id="dmpWirelessSupervision"
              v-model="zoneForm.dmpWirelessSupervision"
              :options="supervisionOptions"
              :disabled="isSaving || !wirelessFields.dmpWirelessSupervision"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="LED Enable" label-for="ledOperation">
            <b-form-checkbox
              id="ledOperation"
              :checked="zoneForm.ledOperation === 'Y'"
              @change="zoneForm.ledOperation = $event ? 'Y' : 'N'"
              :disabled="isSaving || !wirelessFields.ledOperation"
            >
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Disarm Disable" label-for="disarmDisable">
            <b-form-checkbox
              id="disarmDisable"
              :checked="zoneForm.disarmDisable === 'Y'"
              @change="zoneForm.disarmDisable = $event ? 'Y' : 'N'"
              :disabled="isSaving || !wirelessFields.disarmDisable"
            >
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Wireless PIR Pulse Count" label-for="dmpWirelessPirPulseCount">
            <b-form-select
              id="dmpWirelessPirPulseCount"
              v-model="zoneForm.dmpWirelessPirPulseCount"
              :options="pirPulseCountOptions"
              :disabled="isSaving || !wirelessFields.dmpWirelessPirPulseCount"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group
            label="Wireless PIR Pulse Sensitivity"
            label-for="dmpWirelessPirPulseSensitivity"
          >
            <b-form-select
              id="dmpWirelessPirPulseSensitivity"
              v-model="zoneForm.dmpWirelessPirPulseSensitivity"
              :options="sensitivityOptions"
              :disabled="isSaving || !wirelessFields.dmpWirelessPirPulseSensitivity"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Wireless PIR Pet Immunity" label-for="petImmunity">
            <b-form-select
              id="petImmunity"
              v-model="zoneForm.petImmunity"
              :options="petImmunityOptions"
              :disabled="isSaving || !wirelessFields.petImmunity"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { computed, toRefs, getCurrentInstance } from "@vue/composition-api";
import { ValidationProvider } from "vee-validate";
import Swal from "sweetalert2";
import {
  WIRELESS_CONTACT_OPTIONS,
  SUPERVISION_OPTIONS,
  PIR_PULSE_COUNT_OPTIONS,
  PIR_SENSITIVITY_OPTIONS,
  PET_IMMUNITY_OPTIONS,
} from "./useZoneConfig";
import { useWirelessFieldsEnabled, getWirelessDeviceType } from "./useZoneFieldsEnabled";

export default {
  name: "ZoneWireless",
  components: {
    ValidationProvider,
  },
  props: {
    zoneForm: {
      type: Object,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const contactOptions = WIRELESS_CONTACT_OPTIONS;
    const supervisionOptions = SUPERVISION_OPTIONS;
    const pirPulseCountOptions = PIR_PULSE_COUNT_OPTIONS;
    const sensitivityOptions = PIR_SENSITIVITY_OPTIONS;
    const petImmunityOptions = PET_IMMUNITY_OPTIONS;

    // Get field enablement logic based on wireless status and serial number
    const { zoneForm } = toRefs(props);
    const isWireless = computed(() => zoneForm.value.wireless);
    const serialNumber = computed(() => zoneForm.value.dmpWirelessSerial);
    const { wirelessFields, deviceType } = useWirelessFieldsEnabled(isWireless, serialNumber);

    const serialNumberHint = computed(() => {
      const type = deviceType.value;

      const typeMap = {
        'UNKNOWN': 'unknown',
        'UNIVERSAL': 'universal',
        'PIR': 'pir',
        'GLASSBREAK': 'glassbreak',
        'HOLDUP': 'holdup',
        'KEYFOB': 'keyfob',
        'SMOKE_CO': 'smoke_co',
        'FOUR_ZONE_EXPANDER': 'four_zone_expander',
        'WIRELESS_PIR': 'wireless_pir',
        'REPEATER_1100R': 'repeater_1100r',
      };

      const translationKey = typeMap[type] || 'unknown';
      return proxy.$t(`dmp.zoneInformation.deviceTypes.${translationKey}`);
    });

    const handleWirelessChange = (value) => {
      zoneForm.value.wireless = value ? 'Y' : 'N';

      // If wireless is disabled, reset all wireless fields to defaults
      if (!value) {
        zoneForm.value.dmpWirelessSerial = '00000000';
        zoneForm.value.dmpWirelessContact = 'EXTERNAL';
        zoneForm.value.normallyOpen = 'Y';
        zoneForm.value.dmpWirelessSupervision = '240_MINUTES';
        zoneForm.value.ledOperation = 'N';
        zoneForm.value.disarmDisable = 'N';
        zoneForm.value.dmpWirelessPirPulseCount = '4';
        zoneForm.value.dmpWirelessPirPulseSensitivity = 'LOW';
        zoneForm.value.petImmunity = 'OFF';
      }
    };

    const handleSerialNumberChange = async () => {
      const type = getWirelessDeviceType(zoneForm.value.dmpWirelessSerial);

      if (type === 'KEYFOB') {
        await Swal.fire({
          title: proxy.$t("dmp.zoneInformation.messages.warning"),
          html: `
            <p><strong>${proxy.$t("dmp.zoneInformation.messages.serialNumberIsKeyFob")}</strong></p>
            <p>${proxy.$t("dmp.zoneInformation.messages.keyFobsCannotBeProgrammed")}</p>
          `,
          icon: "warning",
          confirmButtonText: "Ok",
          customClass: {
            confirmButton: "btn btn-primary text-capitalize",
          },
          buttonsStyling: false,
        });

        // Set default values for Keyfob
        zoneForm.value.dmpWirelessContact = 'INTERNAL';
        zoneForm.value.dmpWirelessSupervision = 'NONE';
      }
    };

    return {
      contactOptions,
      supervisionOptions,
      pirPulseCountOptions,
      sensitivityOptions,
      petImmunityOptions,
      wirelessFields,
      deviceType,
      serialNumberHint,
      handleWirelessChange,
      handleSerialNumberChange,
    };
  },
};
</script>
